import { buildMergedApprovalItems } from './buildMergedItems';
import type { ApprovalLineItem, ApprovalSignatureDisplay, ApprovalBlockConfig } from './types';
import type { ApprovalLineLayout, ApprovalLineLayoutOptions } from './layouts/types';
// 화면용 양식 css(.approval-section/.approval-cell ...) — source consume 시 Vite가 번들.
import './approval-form.css';

interface ApprovalLineDisplayProps {
  /**
   * 출력 레이아웃 — 호출부(Composer approvalBlock)가 직접 지정한다.
   * 모든 문서가 COMPOSED라 유형으로 layout을 고를 축이 없다.
   */
  layout: ApprovalLineLayout;
  approvalLines: ApprovalLineItem[];
  approvalSignatures: ApprovalSignatureDisplay[];
  /** author 단계 칸 매핑용 임시 작성자 서명/이름 (제출 전 DRAFT) */
  authorSignatureData?: string;
  authorSignerName?: string;
  /** author 단계 칸 클릭 시 작성자 서명 모달 진입점 */
  onAuthorSignatureClick?: () => void;
  /** true면 1단계(안전관리자 lock) 이름을 표시하지 않음 */
  hideFirstAssigneeName?: boolean;
  /** stepper 레이아웃에서 가로 넘침 시 stepper 모드로 렌더 */
  isOverflow?: boolean;
  /** SMS 등에서 OFFLINE 결재 모드(빈 박스 렌더) */
  offlineSignature?: boolean;
  /** 결재란 구성 옵션(P2a-3) — StepperLayout 렌더에만 적용. 미지정 시 CSS 기본. */
  config?: ApprovalBlockConfig;
}

/**
 * 결재란 출력 진입 컴포넌트 (display-only).
 *
 * surface 통일(2026-06-10) 후속 단계1: 데이터 결합(buildMergedApprovalItems)은 공통,
 * 출력 양식은 호출부가 넘긴 layout이 맡는다.
 *
 * - merged items가 비면 null 반환(섹션 미렌더). 표시 여부는 type 하드코딩이 아니라
 *   merged 유무로만 판단한다.
 * - 편집 UI(모달/현장 기본 저장 등)는 포함하지 않는다 — 그건 ApprovalLineSection(Impl)의 책임.
 */
export function ApprovalLineDisplay({
  layout,
  approvalLines,
  approvalSignatures,
  authorSignatureData,
  authorSignerName,
  onAuthorSignatureClick,
  hideFirstAssigneeName,
  isOverflow,
  offlineSignature,
  config,
}: ApprovalLineDisplayProps) {
  const mergedItems = buildMergedApprovalItems(
    approvalLines,
    approvalSignatures,
    authorSignatureData,
    authorSignerName,
  );

  // merged가 비면 섹션 자체를 렌더하지 않음 (type 하드코딩 분기 금지)
  if (mergedItems.length === 0) return null;

  const opts: ApprovalLineLayoutOptions = {
    isOverflow,
    hideFirstAssigneeName,
    onAuthorSignatureClick,
    offlineSignature,
    config,
  };

  return layout.render(mergedItems, opts);
}
